/* ============================================================
   AK Letters — drawer controller

   Opens a right-side drawer with the full Stamped review list
   when the user clicks any element marked
   `[data-ak-reviews-open]`. Two trigger points wire that
   attribute:
     - The Stamped star badge in the PDP buy column
       (snippets/ak-reviews-trigger.liquid)
     - The "Read more letters" button under the pull-quote
       (sections/ak-letters-quote.liquid)

   Reuses the same Stamped widget API endpoint and DOM-card
   parsing approach the pull-quote loader uses (see
   assets/ak-letters-quote.js for endpoint shape + credentials
   piggyback). Reviews are fetched once on first open and
   cached for the lifetime of the page.

   Native dialog APIs are not used here because Safari's
   <dialog> backdrop support is uneven and we want full
   control over the slide-in animation. Focus trap, ESC
   close, scroll lock, and backdrop-click close are all
   implemented manually.
   ============================================================ */
(function () {
  if (window.__akLettersDrawerInit) return;
  window.__akLettersDrawerInit = true;

  /* ── State ────────────────────────────────────────────── */

  var drawer = null;
  var panel = null;
  var bodyEl = null;
  var footerEl = null;
  var moreBtn = null;
  var lastTrigger = null;

  /* Reviews cache. Set once after the first successful fetch. */
  var reviewsCache = null;
  /* How many reviews we've rendered so far (for paginated reveal). */
  var renderedCount = 0;
  var PAGE_SIZE = 10;

  /* ── Initialise on DOM ready ─────────────────────────── */

  function init() {
    drawer = document.getElementById('AkLettersDrawer');
    if (!drawer) return;

    panel = drawer.querySelector('.ak-letters-drawer__panel');
    bodyEl = drawer.querySelector('[data-letters-body]');
    footerEl = drawer.querySelector('[data-letters-footer]');
    moreBtn = drawer.querySelector('[data-letters-more]');

    paintSummaryStars();

    /* Delegated open: any [data-ak-reviews-open] in the document. */
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-ak-reviews-open]');
      if (!trigger) return;
      e.preventDefault();
      open(trigger);
    });

    /* Delegated close: backdrop, close button, or anything
       else marked [data-ak-letters-close] inside the drawer. */
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-ak-letters-close]')) {
        close();
      }
    });

    /* ESC to close while open. */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        close();
      }
    });

    /* Focus trap — Tab cycles within the panel while open. */
    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !drawer.classList.contains('is-open')) return;
      var focusables = getFocusables();
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    if (moreBtn) {
      moreBtn.addEventListener('click', function () {
        renderNextPage();
      });
    }
  }

  /* ── Open / close ──────────────────────────────────── */

  function open(trigger) {
    lastTrigger = trigger || null;

    /* Reveal the drawer container before triggering the
       transition — [hidden] forces display:none, which
       would suppress the transition entirely. */
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ak-letters-drawer-open');

    /* Force a synchronous reflow so the browser commits the
       [hidden] removal + starting transform state BEFORE we
       add .is-open. Without this the browser collapses the
       two style changes into one and no transition runs.
       Originally used requestAnimationFrame nesting, but rAF
       is throttled to ~0Hz in background tabs in Chrome —
       which meant the drawer never opened if the user
       triggered it while the tab wasn't focused (e.g. via a
       keyboard shortcut from another window). The reflow
       trick works regardless of tab focus state. */
    /* eslint-disable-next-line no-unused-expressions */
    drawer.offsetHeight;
    drawer.classList.add('is-open');

    /* Move focus into the panel for screen reader continuity. */
    setTimeout(function () {
      if (panel) panel.focus();
    }, 50);

    /* Suppress the Shopify Inbox chat bubble while the drawer
       is open. CSS does this too (see ak-letters-modal.css),
       but the chat widget ships its own !important display
       rules from a shadow root that sometimes win the cascade.
       Setting inline pointer-events: none on the host element
       guarantees the bubble can't be tapped through the
       drawer's backdrop, regardless of whether display: none
       takes effect. */
    suppressChat();

    /* Fetch on first open. */
    if (!reviewsCache) {
      fetchReviews();
    }
  }

  function close() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ak-letters-drawer-open');
    restoreChat();

    /* Wait for the transition to finish before hiding —
       otherwise the slide-out animation gets clipped. */
    setTimeout(function () {
      if (!drawer.classList.contains('is-open')) {
        drawer.hidden = true;
      }
    }, 450);

    /* Return focus to whatever opened the drawer, for
       a11y continuity. If the trigger was the star badge
       and it's still in the DOM, focus it; otherwise no-op. */
    if (lastTrigger && document.contains(lastTrigger)) {
      try { lastTrigger.focus(); } catch (e) { /* noop */ }
    }
  }

  /* ── Chat widget suppression ────────────────────────────
     Shopify Inbox's #ShopifyChat host sits in the bottom-right
     with z-index 2147483647 (max int) and its display state
     comes from a constructed stylesheet inside a shadow root.
     Pure CSS suppression doesn't always win the cascade, so
     we also set pointer-events: none + visibility: hidden as
     inline !important styles when the drawer opens, and
     restore them on close.

     Updated 2026-07-31: Shopify replaced the old
     <div id="shopify-chat"> + <inbox-online-store-chat
     id="ShopifyChat"> pair with a single <shopify-chat> custom
     element carrying NO id. The old getElementById() lookups
     returned null and these functions became silent no-ops —
     no error, no console warning, just nothing happening.
     Now resolved by SELECTOR (not id) so the element-name
     match works, with the legacy ids kept as fallbacks. */

  var CHAT_SELECTOR = 'shopify-chat, #shopify-chat, #ShopifyChat, inbox-online-store-chat';

  function chatEls() {
    return document.querySelectorAll(CHAT_SELECTOR);
  }

  function suppressChat() {
    chatEls().forEach(function (el) {
      el.style.setProperty('pointer-events', 'none', 'important');
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('opacity', '0', 'important');
    });
  }

  function restoreChat() {
    chatEls().forEach(function (el) {
      el.style.removeProperty('pointer-events');
      el.style.removeProperty('visibility');
      el.style.removeProperty('opacity');
    });
  }

  /* ── Focus trap helper ──────────────────────────────── */

  function getFocusables() {
    if (!panel) return [];
    var selectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ];
    var nodes = panel.querySelectorAll(selectors.join(','));
    return Array.prototype.filter.call(nodes, function (n) {
      return n.offsetWidth > 0 || n.offsetHeight > 0 || n === document.activeElement;
    });
  }

  /* ── Stamped review fetch ───────────────────────────── */

  function fetchReviews() {
    var productId = drawer.dataset.productId;
    if (!productId) {
      renderError();
      return;
    }

    pollForStamped(function (opts) {
      /* Stamped's globalOptions shape varies between theme
         installs. Older apps expose `sId` (numeric shop ID) +
         storeName; newer ones expose `storeUrl`. We need apiKey
         + ONE of the two store identifiers, and build whichever
         URL variant matches what's available. */
      var apiKey = opts.apiKey;
      var sId = opts.sId;
      var storeUrl = opts.storeUrl;
      var storeName = (window.Shopify && window.Shopify.shop) || window.location.host;

      var url = 'https://stamped.io/api/widget'
        + '?productId=' + encodeURIComponent(productId)
        + '&apiKey=' + encodeURIComponent(apiKey)
        + '&take=200';
      if (storeUrl) {
        url += '&storeUrl=' + encodeURIComponent(storeUrl);
      } else {
        url += '&sId=' + encodeURIComponent(sId)
            +  '&storeName=' + encodeURIComponent(storeName);
      }

      fetch(url, { credentials: 'omit' })
        .then(function (r) {
          if (!r.ok) return Promise.reject('HTTP ' + r.status);
          return r.json();
        })
        .then(function (json) {
          var html = json.widget || '';
          if (!html) { renderEmpty(); return; }

          var parser = new DOMParser();
          var doc = parser.parseFromString('<body>' + html + '</body>', 'text/html');
          var cards = doc.querySelectorAll('.stamped-review');

          var reviews = [];
          Array.prototype.forEach.call(cards, function (card) {
            var rating = parseFloat(card.getAttribute('data-rating') || '0');

            /* Same selector strategy as the pull-quote loader.
               .stamped-review-body is a container including
               the title + chrome — the real text is in
               .stamped-review-content-body. */
            var bodyTextEl = card.querySelector('.stamped-review-content-body')
              || card.querySelector('.stamped-review-body p')
              || card.querySelector('.stamped-review-body');
            var bodyText = bodyTextEl ? bodyTextEl.textContent.trim() : '';
            if (!bodyText) return;

            var titleEl = card.querySelector('.stamped-review-header-title, h3');
            var authorEl = card.querySelector('.stamped-review-header .author, .author');
            var locationEl = card.querySelector('.stamped-review-header .review-location, .review-location');
            var dateEl = card.querySelector('.created');

            reviews.push({
              rating: rating,
              title: titleEl ? titleEl.textContent.trim() : '',
              body: bodyText,
              author: authorEl ? authorEl.textContent.trim() : '',
              location: locationEl ? locationEl.textContent.trim() : '',
              date: dateEl ? dateEl.textContent.trim() : ''
            });
          });

          if (!reviews.length) { renderEmpty(); return; }

          reviewsCache = reviews;
          renderedCount = 0;
          renderInitial();
        })
        .catch(function (err) {
          console.warn('[ak-letters-modal] review fetch failed:', err);
          renderError();
        });
    });
  }

  function pollForStamped(cb) {
    var attempts = 0;
    var maxAttempts = 20;
    function attempt() {
      var opts = window.StampedGlobalOptions || {};
      /* Accept either credential shape: apiKey + storeUrl
         (newer Stamped installs) OR apiKey + sId (older). */
      if (opts.apiKey && (opts.storeUrl || opts.sId)) {
        cb(opts);
        return;
      }
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(attempt, 500);
      } else {
        renderError();
      }
    }
    attempt();
  }

  /* ── Rendering ────────────────────────────────────── */

  function renderInitial() {
    bodyEl.innerHTML = '';
    var list = document.createElement('ul');
    list.className = 'ak-letters-drawer__list';
    list.setAttribute('data-letters-list', '');
    bodyEl.appendChild(list);

    renderNextPage();
  }

  function renderNextPage() {
    if (!reviewsCache) return;
    var list = bodyEl.querySelector('[data-letters-list]');
    if (!list) return;

    var slice = reviewsCache.slice(renderedCount, renderedCount + PAGE_SIZE);
    slice.forEach(function (rev) {
      list.appendChild(renderItem(rev));
    });
    renderedCount += slice.length;

    /* Footer visibility — hide once we've rendered everything. */
    if (renderedCount >= reviewsCache.length) {
      footerEl.hidden = true;
    } else {
      footerEl.hidden = false;
    }
  }

  function renderItem(rev) {
    var li = document.createElement('li');
    li.className = 'ak-letters-drawer__item';

    var stars = document.createElement('p');
    stars.className = 'ak-letters-drawer__item-stars';
    stars.setAttribute('aria-label', rev.rating + ' out of 5 stars');
    stars.textContent = starString(rev.rating);
    li.appendChild(stars);

    if (rev.title) {
      var titleEl = document.createElement('h3');
      titleEl.className = 'ak-letters-drawer__item-title';
      titleEl.textContent = rev.title;
      li.appendChild(titleEl);
    }

    var body = document.createElement('p');
    body.className = 'ak-letters-drawer__item-body';
    body.textContent = rev.body;
    li.appendChild(body);

    var byline = document.createElement('p');
    byline.className = 'ak-letters-drawer__item-byline';

    var displayName = abbreviateName(rev.author);
    var city = (rev.location || '').trim();
    if (city.indexOf(',') > -1) {
      city = city.split(',')[0].trim();
    }

    if (displayName) {
      var nameSpan = document.createElement('span');
      nameSpan.className = 'ak-letters-drawer__item-name';
      nameSpan.textContent = displayName;
      byline.appendChild(nameSpan);
    }

    if (displayName && city) {
      byline.appendChild(document.createTextNode(', '));
    }

    if (city) {
      var citySpan = document.createElement('span');
      citySpan.className = 'ak-letters-drawer__item-city';
      citySpan.textContent = city;
      byline.appendChild(citySpan);
    }

    /* Submission date intentionally not rendered. Editorial
       byline pattern: name + place, like a magazine letter
       to the editor. The date adds reader noise without
       carrying editorial weight (and surfaces the age of
       older reviews, which can quietly undermine confidence
       in newer products). */

    if (byline.children.length || byline.textContent.trim()) {
      li.appendChild(byline);
    }

    return li;
  }

  function renderEmpty() {
    bodyEl.innerHTML = '';
    var p = document.createElement('p');
    p.className = 'ak-letters-drawer__empty';
    p.textContent = 'Letters coming soon.';
    bodyEl.appendChild(p);
    footerEl.hidden = true;
  }

  function renderError() {
    bodyEl.innerHTML = '';
    var p = document.createElement('p');
    p.className = 'ak-letters-drawer__empty';
    p.textContent = 'Letters are unavailable right now. Please try again shortly.';
    bodyEl.appendChild(p);
    footerEl.hidden = true;
  }

  /* ── Summary stars (rendered server-side text → glyphs) ─ */

  function paintSummaryStars() {
    var ratingAttr = drawer.dataset.ratingAvg;
    if (!ratingAttr) return;
    var rating = parseFloat(ratingAttr);
    if (!rating || isNaN(rating)) return;
    var slot = drawer.querySelector('[data-letters-summary-stars]');
    if (!slot) return;
    slot.textContent = starString(rating);
  }

  /* ── Helpers ──────────────────────────────────────── */

  function starString(rating) {
    /* Unicode star glyphs — outline (☆) and filled (★).
       Keeps the visual in the same family as the rest of
       the editorial type instead of importing an SVG sprite. */
    var n = Math.round(rating);
    if (n < 0) n = 0;
    if (n > 5) n = 5;
    var out = '';
    for (var i = 0; i < n; i++) out += '★';
    for (var j = n; j < 5; j++) out += '☆';
    return out;
  }

  /* Title-case a single token. Handles hyphens and apostrophes so
     "jean-paul" → "Jean-Paul" and "o'brien" → "O'Brien". Lowercases
     the rest of each piece so all-caps input ("JEFF") also renders
     correctly. Mirrors the function in ak-letters-quote.js — same
     editorial rule applies to both surfaces. */
  function titleCaseToken(token) {
    if (!token) return token;
    return token.split(/([-'])/).map(function (piece) {
      if (piece === '-' || piece === "'") return piece;
      if (!piece) return piece;
      return piece.charAt(0).toUpperCase() + piece.slice(1).toLowerCase();
    }).join('');
  }

  function abbreviateName(raw) {
    if (!raw) return '';
    var parts = raw.trim().split(/\s+/);
    if (parts.length === 1) return titleCaseToken(parts[0]);
    return titleCaseToken(parts[0])
      + ' '
      + titleCaseToken(parts[parts.length - 1]).charAt(0)
      + '.';
  }

  /* ── Boot ──────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
